/**
 * MyTasksDashboardFilters - Barra de filtros enxuta do Dashboard "Minhas Tarefas"
 *
 * Variação do TaskDashboardFilters sem os campos de Responsável e Criado por,
 * já que esta visão é sempre filtrada pelo usuário logado (responsável OU criador).
 *
 * Filtros disponíveis:
 * - Categoria (Select)
 * - Prazo (DueDateQuickFilter)
 * - Data de Criação (Popover + De/Até)
 *
 * @version 1.0.0
 * RELEASE: 2026-04-18 v1.0.0 - Criação inicial (Dashboard "Minhas Tarefas")
 */

import { useMemo, useState } from 'react';
import { CalendarDays, X, FolderTree } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useTaskCategories } from '@/hooks/useTaskCategories';
import { DueDateQuickFilter, type DueDatePreset } from '@/components/tasks/filters/DueDateQuickFilter';
import { EMPTY_DASHBOARD_FILTERS, type TaskDashboardFiltersState } from './TaskDashboardFilters';

interface Props {
  filters: TaskDashboardFiltersState;
  onFiltersChange: (filters: TaskDashboardFiltersState) => void;
}

/**
 * DateFilterRow inline — De/Até com Calendar em Popover
 */
interface DateFilterRowProps {
  label: string;
  fromDate: Date | undefined;
  toDate: Date | undefined;
  onFromChange: (date: Date | undefined) => void;
  onToChange: (date: Date | undefined) => void;
}

function DateFilterRow({ label, fromDate, toDate, onFromChange, onToChange }: DateFilterRowProps) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs flex items-center gap-1.5">
        <CalendarDays className="h-3 w-3" />
        {label}
      </Label>
      <div className="flex gap-2">
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="h-7 flex-1 justify-start text-xs font-normal">
              {fromDate ? format(fromDate, 'dd/MM/yy') : 'De'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start" side="bottom">
            <Calendar
              mode="single"
              selected={fromDate}
              onSelect={onFromChange}
              className="p-3 pointer-events-auto"
              locale={ptBR}
            />
          </PopoverContent>
        </Popover>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="h-7 flex-1 justify-start text-xs font-normal">
              {toDate ? format(toDate, 'dd/MM/yy') : 'Até'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start" side="bottom">
            <Calendar
              mode="single"
              selected={toDate}
              onSelect={onToChange}
              className="p-3 pointer-events-auto"
              locale={ptBR}
            />
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}

/**
 * Componente principal — barra de filtros enxuta do dashboard "Minhas Tarefas"
 */
export function MyTasksDashboardFilters({ filters, onFiltersChange }: Props) {
  const { categories } = useTaskCategories(true);
  const [duePreset, setDuePreset] = useState<DueDatePreset>(null);

  /** Atualizar parcialmente os filtros */
  const update = (patch: Partial<TaskDashboardFiltersState>) => {
    onFiltersChange({ ...filters, ...patch });
  };

  /** Indica se há algum filtro ativo (apenas categoria + prazos + criação) */
  const hasActiveFilters = useMemo(() => {
    return !!(
      filters.categoryId ||
      filters.dueDateFrom ||
      filters.dueDateTo ||
      filters.dueDateIsNull ||
      filters.createdFrom ||
      filters.createdTo
    );
  }, [filters]);

  /** Resumo do filtro de criação (label do botão) */
  const createdLabel = useMemo(() => {
    if (filters.createdFrom && filters.createdTo) {
      return `${format(filters.createdFrom, 'dd/MM')} - ${format(filters.createdTo, 'dd/MM')}`;
    }
    if (filters.createdFrom) return `≥ ${format(filters.createdFrom, 'dd/MM/yy')}`;
    if (filters.createdTo) return `≤ ${format(filters.createdTo, 'dd/MM/yy')}`;
    return 'Dt. Criação';
  }, [filters.createdFrom, filters.createdTo]);

  return (
    <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-none min-w-0 flex-1">
      {/* Categoria */}
      <Select
        value={filters.categoryId || 'all'}
        onValueChange={(v) => update({ categoryId: v === 'all' ? null : v })}
      >
        <SelectTrigger className="h-[22px] w-auto text-xs px-2 gap-1.5 shrink-0 border-0 bg-background rounded-full">
          <FolderTree className="h-3 w-3 text-muted-foreground" />
          <SelectValue placeholder="Categoria" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Todas Categorias</SelectItem>
          {categories.map((c) => (
            <SelectItem key={c.id} value={c.id}>
              {c.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Prazo (presets + personalizada + sem prazo) */}
      <DueDateQuickFilter
        from={filters.dueDateFrom}
        to={filters.dueDateTo}
        noDueDate={!!filters.dueDateIsNull}
        preset={duePreset}
        triggerClassName="h-[22px] text-xs px-2 border-0 bg-background rounded-full"
        onChange={({ from, to, noDueDate, preset }) => {
          setDuePreset(preset);
          update({
            dueDateFrom: from,
            dueDateTo: to,
            dueDateIsNull: noDueDate || undefined,
          });
        }}
      />

      {/* Data de Criação */}
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={cn(
              'h-[22px] text-xs px-2 gap-1.5 shrink-0',
              (filters.createdFrom || filters.createdTo) && 'border-primary text-primary'
            )}
          >
            <CalendarDays className="h-3 w-3" />
            {createdLabel}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-4" align="start">
          <DateFilterRow
            label="Data de Criação"
            fromDate={filters.createdFrom}
            toDate={filters.createdTo}
            onFromChange={(d) => update({ createdFrom: d })}
            onToChange={(d) => update({ createdTo: d })}
          />
          {(filters.createdFrom || filters.createdTo) && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-full mt-3 text-xs"
              onClick={() => update({ createdFrom: undefined, createdTo: undefined })}
            >
              <X className="h-3 w-3 mr-1" /> Limpar criação
            </Button>
          )}
        </PopoverContent>
      </Popover>

      {/* Limpar todos */}
      {hasActiveFilters && (
        <Button
          variant="ghost"
          size="sm"
          className="h-[22px] px-1.5 text-xs text-muted-foreground hover:text-foreground shrink-0"
          onClick={() => {
            onFiltersChange(EMPTY_DASHBOARD_FILTERS);
            setDuePreset(null);
          }}
          title="Limpar filtros"
        >
          <X className="h-3 w-3" />
        </Button>
      )}
    </div>
  );
}
